import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [counter] - The "current / total" counter element (`product-media-gallery-v2-counter`
 *   snippet). Only present in the `spotlight` gallery variant — every method touching it must treat it as optional.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.#mediaScroller?.addEventListener('scroll', this.#syncActiveMedia, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.#mediaScroller?.removeEventListener('scroll', this.#syncActiveMedia);
  }

  /**
   * The element that actually scrolls the full-size media, when it is not the dialog.
   *
   * The theme binds `handleScroll` to the <dialog>, which is right for its own composition: that
   * dialog is full-bleed and the media stack scrolls inside it. The `spotlight` variant turns the
   * dialog into a fixed 1090 × 818 panel with `overflow: clip` and moves the scrolling to the
   * <ul class="dialog-zoomed-gallery">, so a listener bound only to the dialog never fires — the
   * counter and the selected thumbnail froze on the first item however far you scrolled. `scroll`
   * does not bubble, so the second element needs its own listener rather than a delegated one.
   *
   * Derived from an existing ref instead of queried, so it cannot reach outside this component, and
   * returns null when the parent IS the dialog so the theme's own binding is never doubled up.
   *
   * @returns {HTMLElement | null}
   */
  get #mediaScroller() {
    const parent = this.refs.media?.[0]?.parentElement;

    return parent instanceof HTMLElement && parent !== this.refs.dialog ? parent : null;
  }

  /** @type {number} Last index reported by {@link #syncActiveMedia}, to skip redundant DOM writes. */
  #activeIndex = 0;

  /**
   * @type {number | null} Index the gallery BEHIND the dialog has not caught up with yet, or null
   * when it is already in step. Only ever set in the `spotlight` composition — see
   * {@link #notifyMediaSelected}.
   */
  #pendingOutsideSync = null;

  /**
   * Tells the gallery behind the dialog which media the shopper is on — immediately for the theme's
   * own composition, deferred until close for `spotlight`.
   *
   * `<media-gallery>` answers this event with `slideshow.select(index, undefined, { animate: false })`
   * (assets/media-gallery.js:55). On a real PDP that call costs **~850ms**, and not because of the
   * slideshow: ANY forced layout on this page costs that much. Measured on the AirMini PDP, a bare
   * `body.offsetHeight` after an invalidation took **400-680ms** against 8,117 DOM nodes, 345
   * stylesheets, 237 scripts and 900 Yotpo elements. Disabling this variant's own stylesheet changed
   * nothing (932ms vs 1029ms), so it is page weight, not the gallery.
   *
   * Firing it per scroll therefore dragged a ~1s layout behind every gesture inside the lightbox —
   * two long tasks of ~1,190ms for a single scroll — and that lag is what showed up outside the
   * modal. Nothing behind the scrim is visible while the dialog is open, so the sync buys nothing
   * until it closes: the only consumer is `close()`, which morphs the zoomed media into the matching
   * folded slide. One call at the end instead of one per slide.
   *
   * The theme's composition keeps the immediate dispatch: its dialog is full-bleed with no scrim and
   * 17 live instances depend on the current behaviour.
   *
   * @param {number} index - The index of the media now on screen.
   */
  #notifyMediaSelected(index) {
    if (!this.#mediaScroller) {
      this.dispatchEvent(new ZoomMediaSelectedEvent(index));
      return;
    }

    this.#pendingOutsideSync = index;
  }

  /** Hands the deferred index to the gallery behind the dialog. No-op when there is nothing pending. */
  #flushOutsideSync() {
    const index = this.#pendingOutsideSync;
    if (index == null) return;

    this.#pendingOutsideSync = null;
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Keeps the counter and the thumbnail selection in step with the media actually on screen,
   * computed straight from the scroll offset.
   *
   * NOT `handleScroll`. That one resolves the active media with an async IntersectionObserver, which
   * is the right tool for the theme's free-scrolling stack but misbehaves here: clicking a thumbnail
   * sets the counter synchronously and then, a beat later, an observer callback measured mid-flight
   * overwrites it with the previous position. Measured: clicking thumbnail 7 scrolled to media 7
   * while the counter settled on 2.
   *
   * In this composition every slide is exactly the scroller's height (the CSS gives them
   * `height: 100%`), so the index is the offset over that height — exact, synchronous, and immune to
   * the ordering problem. The hi-res swap and `ZoomMediaSelectedEvent` are kept for parity with
   * `handleScroll`, which this replaces on the media scroller only; the dialog keeps the theme's
   * original binding.
   */
  #syncActiveMedia = () => {
    const scroller = this.#mediaScroller;
    const { media, thumbnails } = this.refs;

    if (!scroller || !scroller.clientHeight || !media?.length || !thumbnails) return;

    const index = Math.min(
      media.length - 1,
      Math.max(0, Math.round(scroller.scrollTop / scroller.clientHeight))
    );

    if (index === this.#activeIndex) return;
    this.#activeIndex = index;

    this.#updateCounter(index);

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    const active = media[index];
    if (active) this.#loadHighResolutionImage(active);

    this.#notifyMediaSelected(index);
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    try {
      event.preventDefault();

      const { dialog, media, thumbnails } = this.refs;
      const targetImage = media[index];
      const targetThumbnail = thumbnails.children[index];

      const open = () => {
        dialog.showModal();
        // Opening on the media already counted (#activeIndex) fires no sync, so set it here too.
        this.#updateCounter(index);

        for (const target of [targetThumbnail, targetImage]) {
          target?.scrollIntoView({ behavior: 'instant' });
        }
      };

      /** @type {HTMLElement | null} */
      const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

      if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

      const transitionName = `gallery-item`;
      sourceImage.style.setProperty('view-transition-name', transitionName);

      await startViewTransition(() => {
        open();
        sourceImage.style.removeProperty('view-transition-name');
        targetImage.style.setProperty('view-transition-name', transitionName);
      });

      targetImage.style.removeProperty('view-transition-name');

      this.selectThumbnail(index, { behavior: 'instant' });
    } catch (error) {
      console.error('ZoomDialog.open failed:', error);
    }
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Updates the "current / total" counter (`ref="counter"`) to a 1-based index.
   * The counter only exists in the `spotlight` gallery variant, so this is a no-op
   * whenever it is absent from the DOM — never throws for the `default` variant.
   *
   * Also hands the shown media's aspect ratio (the `--ratio` that snippets/product-media.liquid
   * writes on `.product-media`) to the dialog as `--pmg-lb-active-ratio`. On the 2026 templates the
   * desktop photo shrinks to its own size inside the card and the chip follows its top-right corner
   * (assets/sl-machine-buy-column.css); the CSS does the maths, so nothing is measured here.
   *
   * @param {number} index - The 0-based index of the currently visible media.
   */
  #updateCounter(index) {
    const { counter, dialog, media } = this.refs;
    if (!counter) return;

    counter.textContent = `${index + 1}`;

    const productMedia = media?.[index]?.querySelector('.product-media');
    if (!(productMedia instanceof HTMLElement)) return;

    const ratio = parseFloat(productMedia.style.getPropertyValue('--ratio'));
    if (ratio > 0) dialog.style.setProperty('--pmg-lb-active-ratio', `${ratio}`);
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    this.#updateCounter(activeIndex);

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    try {
      // The gallery behind the scrim only has to be right once: now. See #notifyMediaSelected.
      this.#flushOutsideSync();

      const { dialog, media } = this.refs;

      if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

      // Find the most visible image using IntersectionObserver
      const mostVisibleElement = await getMostVisibleElement(media);

      // Get the index and set up transition
      const activeIndex = media.indexOf(mostVisibleElement);
      const transitionName = `gallery-item`;

      const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
        this.closest('media-gallery')
      );

      const slideshowActive = mediaGallery?.presentation === 'carousel';

      const slide = slideshowActive
        ? mediaGallery.slideshow?.slides?.[activeIndex]
        : mediaGallery?.media?.[activeIndex];

      if (!slide) return this.closeDialog();

      dialog.classList.add('dialog--closed');

      await onAnimationEnd(this.refs.thumbnails);

      mostVisibleElement.style.setProperty('view-transition-name', transitionName);

      await startViewTransition(() => {
        mostVisibleElement.style.removeProperty('view-transition-name');
        slide.style.setProperty('view-transition-name', transitionName);
        this.closeDialog();
      });

      slide.style.removeProperty('view-transition-name');
      dialog.classList.remove('dialog--closed');
    } catch (error) {
      console.error('ZoomDialog.close failed:', error);
    }
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Closes the dialog when the click lands on the backdrop instead of on the panel
   * ("light dismiss"). Wired with `on:click="/handleLightDismiss"` on the <dialog>, and only in the
   * `spotlight` gallery variant — the `default` variant renders a full-bleed dialog with no backdrop
   * to click, and 17 live instances rely on its current behaviour.
   *
   * A click on a <dialog>'s ::backdrop reports the dialog itself as the event target, so anything
   * else means a child was clicked and is left alone. What "backdrop" means then depends on the
   * composition:
   *
   *   · Desktop — the dialog IS the 1090 × 818 panel, so only a point outside its box is backdrop.
   *     Its own 76/82 padding is visible panel and must not dismiss.
   *   · Mobile — the dialog is a transparent full-viewport sheet with the gallery floating on it, so
   *     there is no outside to click: a hit on the sheet itself is the backdrop.
   *
   * Measuring the box tells the two apart without duplicating the CSS breakpoint in JS.
   *
   * @param {MouseEvent} event - The click event.
   */
  handleLightDismiss(event) {
    const { dialog } = this.refs;
    // The original target, not `event.target`: assets/component.js hands this handler a proxy whose
    // `target` is the element carrying `on:click` — the dialog itself — so a click on a child with no
    // handler of its own (a playing video, its slide) passed this guard and closed the phone
    // lightbox. `composedPath()` is bound to the real event.
    if (event.composedPath()[0] !== dialog) return;

    const { top, right, bottom, left, width, height } = dialog.getBoundingClientRect();
    const fillsViewport = width >= window.innerWidth - 1 && height >= window.innerHeight - 1;
    const outsidePanel =
      event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom;

    if (fillsViewport || outsidePanel) this.close();
  }

  /**
   * Advances to the next media, reusing `selectThumbnail` so the thumbnail rail, the counter and the
   * media scroller all stay in step. Wired from the chevron under the lightbox thumbnail column in
   * the `spotlight` variant. A no-op on the last item — the rail does not wrap, so neither does this.
   */
  next() {
    const { thumbnails } = this.refs;
    if (!thumbnails) return;

    const buttons = Array.from(thumbnails.querySelectorAll('button'));
    const current = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    const index = current + 1;

    if (index >= buttons.length) return;

    this.selectThumbnail(index, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    try {
      const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
      this.selectThumbnail(index, { behavior });
    } catch (error) {
      console.error('ZoomDialog.handleThumbnailClick failed:', error);
    }
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    this.#updateCounter(index);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.#notifyMediaSelected(index);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
